import { httpClient, baseUrl, getAuthHeaders } from 'src/pages/admin/providers/AppDataProvider';
import type {
    DashboardV3UserDataParams,
    DashboardV3UserDataResp,
    DashboardV3UserKpiResp,
    ExportDashboardV3UserDataParams,
    DashboardV3TradingDataParams,
    DashboardV3TradingDataResp,
    DashboardV3TradingKpiResp,
    ExportDashboardV3TradingDataParams,
    DashboardV3PerpetualDetailParams,
    DashboardV3PerpetualDetailResp,
    DashboardV3PerpetualDetailRow,
    DashboardV3DepositKpiParams,
    DashboardV3DepositKpiResp,
    DashboardV3DepositListParams,
    DashboardV3DepositListResp,
    ExportDashboardV3DepositParams,
    DashboardV3OverviewFunnelResp,
    DashboardV3OverviewRetentionMatrixResp,
    DashboardV3OverviewKpiParams,
    DashboardV3OverviewKpiResp,
    DashboardV3OverviewProductKpiParams,
    DashboardV3OverviewProductKpiResp,
    DashboardV3OverviewTrendParams,
    DashboardV3OverviewTrendResp,
    ReferralDetailResp,
    ReferralKpiResp,
    ReferralQueryParams,
    ReferralSubsExportParams,
    ReferralUsersParams,
    ReferralUsersResp,
    ReferralProductType,
    DashboardV3RetentionProductType,
    DashboardV3RetentionGranularity,
    DashboardV3RetentionKpiResp,
    DashboardV3RetentionCohortsResp,
} from './dashboardV3.types';

const REFERRAL_BASE = `${baseUrl}/referral`;
const DASHBOARD_V3_USERS_BASE = `${baseUrl}/dashboard-v3/users`;
const DASHBOARD_V3_TRADING_BASE = `${baseUrl}/dashboard-v3/trading`;
const DASHBOARD_V3_DEPOSITS_BASE = `${baseUrl}/dashboard-v3/deposits`;
const DASHBOARD_V3_RETENTION_BASE = `${baseUrl}/dashboard-v3/retention`;
const DASHBOARD_V3_OVERVIEW_BASE = `${baseUrl}/dashboard-v3/overview`;
const PERPETUAL_TRADING_BASE = `${baseUrl}/perpetual-trading`;
const DASHBOARD_V3_EXPORT_PAGE_SIZE = 10000;

function buildQuery(params: Record<string, any>) {
    const sp = new URLSearchParams();

    Object.entries(params).forEach(([key, value]) => {
        if (value === undefined || value === null || value === '') return;
        sp.set(key, String(value));
    });

    const qs = sp.toString();
    return qs ? `?${qs}` : '';
}

async function apiGet<T>(path: string, params: Record<string, any> = {}) {
    const url = `${path}${buildQuery(params)}`;
    const { json } = await httpClient(url, { method: 'GET' });
    return json as T;
}

function parseFilenameFromDisposition(disposition: string | null) {
    if (!disposition) return null;

    const utf8Match = disposition.match(/filename\*=UTF-8''([^;]+)/i);
    if (utf8Match?.[1]) {
        return decodeURIComponent(utf8Match[1]);
    }

    const normalMatch = disposition.match(/filename\s*=\s*"([^"]+)"/i);
    return normalMatch?.[1] ?? null;
}

// -------------------------- Referral Tab --------------------------

/**
 * 只有邀请首页 KPI 需要传时间范围。
 */
export function getReferralKpi(params: ReferralQueryParams) {
    return apiGet<ReferralKpiResp>(`${REFERRAL_BASE}/kpi`, params);
}

export function getReferralUsers(params: ReferralUsersParams) {
    return apiGet<ReferralUsersResp>(`${REFERRAL_BASE}/users`, params);
}

/**
 * Detail KPI 卡片 + Detail 列表都不传时间范围。
 */
export function getReferralDetail(
    idOrCode: string,
    params: { productType?: ReferralProductType } = {}
) {
    return apiGet<ReferralDetailResp>(`${REFERRAL_BASE}/users/${idOrCode}/detail`, params);
}

export async function exportReferralUsers(params: ReferralUsersParams & { format?: 'csv' | 'excel' }) {
    const url = `${REFERRAL_BASE}/users/export${buildQuery({
        ...params,
        format: params.format ?? 'csv',
    })}`;

    const headers = await getAuthHeaders({ Accept: 'text/csv,*/*' });

    const res = await fetch(url, {
        method: 'POST',
        headers,
    });

    if (!res.ok) {
        const text = await res.text().catch(() => '');
        throw new Error(text || `Export failed (${res.status})`);
    }

    const blob = await res.blob();
    const filename =
        parseFilenameFromDisposition(res.headers.get('content-disposition')) ||
        `referral-users-${Date.now()}.csv`;

    return { blob, filename };
}

export async function exportReferralSubs(
    idOrCode: string,
    params: ReferralSubsExportParams = {}
) {
    const url = `${REFERRAL_BASE}/users/${idOrCode}/subs/export${buildQuery({
        filter: params.filter ?? 'all',
        format: params.format ?? 'csv',
        productType: params.productType,
    })}`;

    const headers = await getAuthHeaders({ Accept: 'text/csv,*/*' });

    const res = await fetch(url, {
        method: 'POST',
        headers,
    });

    if (!res.ok) {
        const text = await res.text().catch(() => '');
        throw new Error(text || `Export failed (${res.status})`);
    }

    const blob = await res.blob();
    const filename =
        parseFilenameFromDisposition(res.headers.get('content-disposition')) ||
        `referral-subs-${Date.now()}.csv`;

    return { blob, filename };
}

// -------------------------- Users Tab --------------------------

export function getDashboardV3UserKpi(
    params: Pick<DashboardV3UserDataParams, 'range' | 'startDate' | 'endDate'>
) {
    return apiGet<DashboardV3UserKpiResp>(`${DASHBOARD_V3_USERS_BASE}/kpi`, params);
}

export function getDashboardV3Users(params: DashboardV3UserDataParams) {
    return apiGet<DashboardV3UserDataResp>(DASHBOARD_V3_USERS_BASE, params);
}

export async function exportDashboardV3UserData(params: ExportDashboardV3UserDataParams) {
    const url = `${DASHBOARD_V3_USERS_BASE}/export${buildQuery({
        ...params,
        format: params.format ?? 'csv',
    })}`;

    const headers = await getAuthHeaders({ Accept: 'text/csv,*/*' });

    const res = await fetch(url, {
        method: 'POST',
        headers,
    });

    if (!res.ok) {
        const text = await res.text().catch(() => '');
        throw new Error(text || `Export failed (${res.status})`);
    }

    const blob = await res.blob();
    const filename =
        parseFilenameFromDisposition(res.headers.get('content-disposition')) ||
        `dashboard-v3-users-${Date.now()}.csv`;

    return { blob, filename };
}

// -------------------------- Deposits Tab --------------------------

export function getDashboardV3DepositKpi(params: DashboardV3DepositKpiParams) {
    return apiGet<DashboardV3DepositKpiResp>(`${DASHBOARD_V3_DEPOSITS_BASE}/kpi`, params);
}

export function getDashboardV3Deposits(params: DashboardV3DepositListParams) {
    return apiGet<DashboardV3DepositListResp>(DASHBOARD_V3_DEPOSITS_BASE, {
        ...params,
        page: params.page + 1,
    });
}

export async function exportDashboardV3Deposits(params: ExportDashboardV3DepositParams) {
    const url = `${DASHBOARD_V3_DEPOSITS_BASE}/export${buildQuery({
        ...params,
        format: params.format ?? 'csv',
    })}`;

    const headers = await getAuthHeaders({ Accept: 'text/csv,*/*' });
    const res = await fetch(url, {
        method: 'POST',
        headers,
    });

    if (!res.ok) {
        const text = await res.text().catch(() => '');
        throw new Error(text || `Export failed (${res.status})`);
    }

    const blob = await res.blob();
    const filename =
        parseFilenameFromDisposition(res.headers.get('content-disposition')) ||
        `dashboard-v3-deposits-${Date.now()}.csv`;

    return { blob, filename };
}

// -------------------------- Trading Tab --------------------------

export function getDashboardV3TradingKpi(
    params: Pick<DashboardV3TradingDataParams, 'range' | 'startDate' | 'endDate' | 'productType'>
) {
    return apiGet<DashboardV3TradingKpiResp>(`${DASHBOARD_V3_TRADING_BASE}/kpi`, params);
}

export function getDashboardV3Trading(params: DashboardV3TradingDataParams) {
    // 前端分页（MUI TablePagination）是 0-based，后端要求 page >= 1
    return apiGet<DashboardV3TradingDataResp>(DASHBOARD_V3_TRADING_BASE, {
        ...params,
        page: params.page + 1,
    });
}

export async function getAllDashboardV3TradingRows(
    params: Omit<DashboardV3TradingDataParams, 'page' | 'size'>
) {
    const rows: DashboardV3TradingDataResp['data']['list'] = [];
    let page = 0;
    let total: number | undefined;
    let hasMore = true;

    while (hasMore) {
        // eslint-disable-next-line no-await-in-loop
        const resp = await getDashboardV3Trading({
            ...params,
            page,
            size: DASHBOARD_V3_EXPORT_PAGE_SIZE,
        });
        const currentRows = resp?.data?.list ?? [];
        const respTotal = Number(resp?.total);

        if (Number.isFinite(respTotal) && respTotal >= 0) {
            total = respTotal;
        }

        rows.push(...currentRows);

        hasMore =
            currentRows.length > 0 &&
            (total !== undefined && total > 0
                ? rows.length < total
                : currentRows.length >= DASHBOARD_V3_EXPORT_PAGE_SIZE);

        if (hasMore) {
            page += 1;
        }
    }

    return total !== undefined && total > 0 ? rows.slice(0, total) : rows;
}

/**
 * 合约 tab 的 Trading Detail：复用 perpetual-trading 逐笔成交接口。
 * 状态固定只取 FILLED，并按 userId / 钱包地址 / 明细时间范围过滤。
 * Dashboard V3 明细列表不受右上角时间范围影响，只传明细区域选择的 from/to。
 * 该接口与 perpetual-trading 列表保持一致的 query / sort 入参形式。
 */
export function getDashboardV3PerpetualDetail(params: DashboardV3PerpetualDetailParams) {
    const { page, size, userId, walletAddress, startDate, endDate, sortBy, sortOrder } = params;

    const query: Record<string, any> = { status: 'FILLED' };
    if (userId) query.userId = userId;
    if (walletAddress) query.walletAddress = walletAddress;
    if (startDate) query.from = startDate;
    if (endDate) query.to = endDate;

    return apiGet<DashboardV3PerpetualDetailResp>(PERPETUAL_TRADING_BASE, {
        sort: JSON.stringify({
            field: sortBy || 'createdAtRaw',
            order: (sortOrder || 'desc').toUpperCase(),
        }),
        // 前端分页（MUI TablePagination）是 0-based，后端要求 page >= 1
        page: page + 1,
        size,
        query: JSON.stringify(query),
    });
}

function getPerpetualDetailRows(resp: DashboardV3PerpetualDetailResp) {
    return resp?.items ?? resp?.data ?? resp?.list ?? resp?.records ?? [];
}

export async function getAllDashboardV3PerpetualDetail(
    params: Omit<DashboardV3PerpetualDetailParams, 'page' | 'size'>
) {
    const rows: DashboardV3PerpetualDetailRow[] = [];
    let page = 0;
    let total: number | undefined;
    let hasMore = true;

    while (hasMore) {
        // eslint-disable-next-line no-await-in-loop
        const resp = await getDashboardV3PerpetualDetail({
            ...params,
            page,
            size: DASHBOARD_V3_EXPORT_PAGE_SIZE,
        });
        const currentRows = getPerpetualDetailRows(resp);
        const respTotal = Number(resp?.total);

        if (Number.isFinite(respTotal) && respTotal >= 0) {
            total = respTotal;
        }

        rows.push(...currentRows);

        hasMore =
            currentRows.length > 0 &&
            (total !== undefined && total > 0
                ? rows.length < total
                : currentRows.length >= DASHBOARD_V3_EXPORT_PAGE_SIZE);

        if (hasMore) {
            page += 1;
        }
    }

    return total !== undefined && total > 0 ? rows.slice(0, total) : rows;
}

export async function exportDashboardV3TradingData(params: ExportDashboardV3TradingDataParams) {
    const url = `${DASHBOARD_V3_TRADING_BASE}/export${buildQuery({
        ...params,
        format: params.format ?? 'csv',
    })}`;

    const headers = await getAuthHeaders({ Accept: 'text/csv,*/*' });

    const res = await fetch(url, {
        method: 'POST',
        headers,
    });

    if (!res.ok) {
        const text = await res.text().catch(() => '');
        throw new Error(text || `Export failed (${res.status})`);
    }

    const blob = await res.blob();
    const filename =
        parseFilenameFromDisposition(res.headers.get('content-disposition')) ||
        `dashboard-v3-trading-${Date.now()}.csv`;

    return { blob, filename };
}

// -------------------------- Retention Tab --------------------------

/**
 * 日/周/月三张 KPI 卡固定返回，不受右上角粒度切换影响，所以只需要传产品。
 */
export function getDashboardV3RetentionKpi(params: {
    productType: DashboardV3RetentionProductType;
}) {
    return apiGet<DashboardV3RetentionKpiResp>(`${DASHBOARD_V3_RETENTION_BASE}/kpi`, params);
}

/**
 * 留存率趋势图与队列留存矩阵同源：矩阵取每行全部期数，趋势图取每行第 1 期，
 * 所以一次请求即可，不用分开调。
 */
export function getDashboardV3RetentionCohorts(params: {
    productType: DashboardV3RetentionProductType;
    granularity: DashboardV3RetentionGranularity;
}) {
    return apiGet<DashboardV3RetentionCohortsResp>(`${DASHBOARD_V3_RETENTION_BASE}/cohorts`, params);
}


// -------------------------- Overview Tab --------------------------

export function getDashboardV3OverviewKpi(params: DashboardV3OverviewKpiParams) {
    return apiGet<DashboardV3OverviewKpiResp>(`${DASHBOARD_V3_OVERVIEW_BASE}/kpi`, params);
}

export function getDashboardV3OverviewFunnel(params: DashboardV3OverviewKpiParams) {
    return apiGet<DashboardV3OverviewFunnelResp>(`${DASHBOARD_V3_OVERVIEW_BASE}/funnel`, params);
}

/** 四个交易类型各一张留存矩阵，一次返回；不受右上角时间筛选影响。 */
export function getDashboardV3OverviewRetentionMatrix() {
    return apiGet<DashboardV3OverviewRetentionMatrixResp>(`${DASHBOARD_V3_OVERVIEW_BASE}/retention-matrix`);
}

export function getDashboardV3OverviewProductKpi(params: DashboardV3OverviewProductKpiParams) {
    return apiGet<DashboardV3OverviewProductKpiResp>(`${DASHBOARD_V3_OVERVIEW_BASE}/product/kpi`, params);
}

export function getDashboardV3OverviewProductTrend(params: DashboardV3OverviewTrendParams) {
    return apiGet<DashboardV3OverviewTrendResp>(`${DASHBOARD_V3_OVERVIEW_BASE}/product/trend`, params);
}